import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Plus, Users, Edit, Trash2, Search, AlertTriangle, Eye, Download, ArrowUpDown, ArrowUp, ArrowDown, Building2, User, Zap } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import AddressAutocomplete from './AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomersManagementEnhanced = ({ getAuthHeaders }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [ignoreDuplicates, setIgnoreDuplicates] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  
  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [formData, setFormData] = useState({
    company_id: '',
    name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    address_3: '',
    city: '',
    county: '',
    postcode: '',
    customer_type: 'individual',
    tags: [],
    source: '',
    status: 'active',
    notes: '',
    email_opt_in: true,
    sms_opt_in: true,
    whatsapp_opt_in: false
  });
  const [tagInput, setTagInput] = useState('');
  const [counties, setCounties] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [isSourceDialogOpen, setIsSourceDialogOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');

  useEffect(() => {
    fetchCounties();
    fetchLeadSources();
    fetchCustomers();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchCustomers();
    }, 300); // Debounce search by 300ms
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, statusFilter, sourceFilter]);

  const fetchCounties = async () => {
    try {
      const response = await axios.get(`${API}/counties`, getAuthHeaders());
      setCounties(response.data);
    } catch (error) {
      console.error('Failed to fetch counties:', error);
    }
  };

  const fetchLeadSources = async () => {
    try {
      const response = await axios.get(`${API}/lead-sources`, getAuthHeaders());
      setLeadSources(response.data.filter(source => source.is_active));
    } catch (error) {
      console.error('Failed to fetch lead sources:', error);
    }
  };

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) params.append('search', searchTerm.trim());
      if (statusFilter) params.append('status', statusFilter);
      if (sourceFilter && sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const url = params.toString() ? `${API}/customers/search?${params}` : `${API}/customers`;
      console.log('Fetching customers from:', url);
      const response = await axios.get(url, getAuthHeaders());
      console.log('Customers response:', response.data);
      
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setCustomers(response.data);
      } else {
        console.error('Unexpected response format:', response.data);
        setCustomers([]);
        toast.error('Unexpected response format from server');
      }
    } catch (error) {
      console.error('Fetch customers error:', error);
      console.error('Error response:', error.response);
      setCustomers([]);
      toast.error(error.response?.data?.detail || 'Failed to fetch customers');
    } finally {
      setIsLoading(false);
    }
  };

  const checkDuplicates = async () => {
    if (!formData.name) return;
    
    try {
      const response = await axios.post(
        `${API}/customers/check-duplicate`,
        {
          name: formData.name,
          email: formData.email || null,
          phone: formData.phone || null,
          company_id: formData.company_id
        },
        getAuthHeaders()
      );
      
      if (response.data.is_duplicate) {
        setDuplicateCheck(response.data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Duplicate check failed:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check duplicates before creating new customer (unless user chose to ignore them)
    if (!editingCustomer && !ignoreDuplicates) {
      const hasDuplicates = await checkDuplicates();
      if (hasDuplicates) {
        // Don't proceed, let the user see the duplicate warning and choose to continue
        return;
      }
    }
    
    try {
      // Clean up form data - remove empty strings and replace with null
      const submitData = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        
        // Skip company_id as it will be set by backend
        if (key === 'company_id') return acc;
        
        // Handle different data types
        if (value === '' || value === null || value === undefined) {
          // Only include non-empty values, let backend use defaults
          return acc;
        } else if (Array.isArray(value) && value.length === 0) {
          // Don't include empty arrays
          return acc;
        } else {
          acc[key] = value;
        }
        
        return acc;
      }, {});
      
      console.log('Submitting customer data:', submitData);
      
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, submitData, getAuthHeaders());
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API}/customers`, submitData, getAuthHeaders());
        toast.success('Customer created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error('Customer operation error:', error);
      
      let errorMessage = 'Operation failed';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            // Handle Pydantic validation errors
            errorMessage = error.response.data.detail
              .map(err => `${err.loc ? err.loc.join(' ') : 'Field'}: ${err.msg}`)
              .join(', ');
          } else {
            errorMessage = error.response.data.detail;
          }
        } else if (Array.isArray(error.response.data)) {
          // Handle direct validation error array
          errorMessage = error.response.data
            .map(err => `${err.loc ? err.loc.join(' ') : 'Field'}: ${err.msg}`)
            .join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await axios.delete(`${API}/customers/${id}`, getAuthHeaders());
        toast.success('Customer deleted successfully');
        fetchCustomers();
      } catch (error) {
        toast.error('Failed to delete customer');
      }
    }
  };

  const handleExportCustomer = async (customerId, customerName) => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer-${customerName.replace(/[^a-zA-Z0-9]/g, '-')}-export.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Customer data exported successfully');
    } catch (error) {
      toast.error('Failed to export customer data');
    }
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const formatAddress = (customer) => {
    const parts = [];
    if (customer.address_1) parts.push(customer.address_1);
    if (customer.city) parts.push(customer.city);
    if (customer.county) parts.push(customer.county);
    if (customer.postcode) parts.push(customer.postcode);
    return parts.join(', ') || 'No address';
  };

  const getCustomerTypeIcon = (customerType) => {
    switch (customerType) {
      case 'business':
      case 'company':
        return <Building2 className="w-4 h-4 text-blue-600" title="Business Customer" />;
      case 'individual':
      default:
        return <User className="w-4 h-4 text-green-600" title="Individual Customer" />;
    }
  };

  const handleContinueAnyway = async () => {
    // Clear duplicate warnings and proceed with submission
    setDuplicateCheck(null);
    setIgnoreDuplicates(true);
    
    // Now proceed with the actual customer creation
    try {
      // Clean up form data - remove empty strings and replace with null
      const submitData = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        
        // Skip company_id as it will be set by backend
        if (key === 'company_id') return acc;
        
        // Handle different data types
        if (value === '' || value === null || value === undefined) {
          // Only include non-empty values, let backend use defaults
          return acc;
        } else if (Array.isArray(value) && value.length === 0) {
          // Don't include empty arrays
          return acc;
        } else {
          acc[key] = value;
        }
        
        return acc;
      }, {});
      
      console.log('Creating customer despite duplicates:', submitData);
      
      await axios.post(`${API}/customers`, submitData, getAuthHeaders());
      toast.success('Customer created successfully');
      
      setIsDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error('Customer creation error:', error);
      
      let errorMessage = 'Failed to create customer';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            // Handle Pydantic validation errors
            errorMessage = error.response.data.detail
              .map(err => `${err.loc ? err.loc.join(' ') : 'Field'}: ${err.msg}`)
              .join(', ');
          } else {
            errorMessage = error.response.data.detail;
          }
        } else if (Array.isArray(error.response.data)) {
          // Handle direct validation error array
          errorMessage = error.response.data
            .map(err => `${err.loc ? err.loc.join(' ') : 'Field'}: ${err.msg}`)
            .join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };
  // Filter and sort customers
  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = customers;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (customer.phone && customer.phone.includes(searchTerm)) ||
        (customer.postcode && customer.postcode.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(customer => customer.status === statusFilter);
    }

    // Apply source filter
    if (sourceFilter) {
      filtered = filtered.filter(customer => customer.source === sourceFilter);
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortField) {
          case 'name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'email':
            aValue = a.email || '';
            bValue = b.email || '';
            break;
          case 'phone':
            aValue = a.phone || '';
            bValue = b.phone || '';
            break;
          case 'address':
            aValue = formatAddress(a);
            bValue = formatAddress(b);
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'source':
            aValue = a.source || '';
            bValue = b.source || '';
            break;
          case 'contact_count':
            aValue = a.contact_count || 0;
            bValue = b.contact_count || 0;
            break;
          case 'vehicle_count':
            aValue = a.vehicle_count || 0;
            bValue = b.vehicle_count || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [customers, searchTerm, statusFilter, sourceFilter, sortField, sortDirection]);

  const handleAddNewSource = async () => {
    if (!newSourceName.trim()) {
      toast.error('Please enter a source name');
      return;
    }

    try {
      await axios.post(`${API}/lead-sources`, {
        name: newSourceName.trim(),
        description: `Added from customer form`,
        is_active: true
      }, getAuthHeaders());
      
      toast.success(`Lead source "${newSourceName}" added successfully`);
      
      // Refresh lead sources and select the new one
      await fetchLeadSources();
      setFormData({ ...formData, source: newSourceName.trim() });
      
      // Close dialog and reset
      setIsSourceDialogOpen(false);
      setNewSourceName('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to add lead source';
      toast.error(errorMessage);
    }
  };

  const handleAddressSelect = (addressData) => {
    // Debug: Log the response to see the structure
    console.log('Address data received:', addressData);
    
    // Auto-populate address fields from getaddress.io response with better logic
    const addressUpdate = {
      address_1: addressData.line_1 || '',
      address_2: '',
      address_3: '',
      city: addressData.town_or_city || '',
      county: addressData.county || '',
      postcode: addressData.postcode || ''
    };
    
    // Better handling of address lines - use available data intelligently
    if (addressData.line_2) {
      addressUpdate.address_2 = addressData.line_2;
    } else if (addressData.locality && addressData.locality !== addressData.town_or_city) {
      // Use locality (like "Sarisbury Green") if it's different from the main city
      addressUpdate.address_2 = addressData.locality;
    } else if (addressData.district && addressData.district !== addressData.town_or_city) {
      // Use district if available and different from city
      addressUpdate.address_2 = addressData.district;
    }
    
    if (addressData.line_3) {
      addressUpdate.address_3 = addressData.line_3;
    } else if (addressData.line_4) {
      addressUpdate.address_3 = addressData.line_4;
    }
    
    // If we still don't have address_2 and there's formatted_address, try to parse it
    if (!addressUpdate.address_2 && addressData.formatted_address) {
      const addressParts = addressData.formatted_address;
      // Try to extract area/district from formatted address
      if (Array.isArray(addressParts) && addressParts.length > 1) {
        // formatted_address is often ["16 Watkin Terrace", "", "", "Northampton", "Northamptonshire"]
        for (let i = 1; i < addressParts.length - 2; i++) { // Skip first (line1) and last two (city, county)
          if (addressParts[i] && addressParts[i].trim()) {
            if (!addressUpdate.address_2) {
              addressUpdate.address_2 = addressParts[i].trim();
            } else if (!addressUpdate.address_3) {
              addressUpdate.address_3 = addressParts[i].trim();
            }
            break; // Take the first available one
          }
        }
      }
    }
    
    setFormData(prev => ({
      ...prev,
      ...addressUpdate
    }));
    
    // Log what we're setting for debugging
    console.log('Setting address fields:', addressUpdate);
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      name: '',
      email: '',
      phone: '',
      address_1: '',
      address_2: '',
      address_3: '',
      city: '',
      county: '',
      postcode: '',
      customer_type: 'individual',
      tags: [],
      source: '',
      status: 'active',
      notes: '',
      email_opt_in: true,
      sms_opt_in: true,
      whatsapp_opt_in: false
    });
    setEditingCustomer(null);
    setDuplicateCheck(null);
    setIgnoreDuplicates(false);
    setTagInput('');
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      company_id: customer.company_id,
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      address_1: customer.address_1 || customer.address || '', // Handle legacy data
      address_2: customer.address_2 || '',
      address_3: customer.address_3 || '',
      city: customer.city || '',
      county: customer.county || '',
      postcode: customer.postcode || '',
      customer_type: customer.customer_type,
      tags: customer.tags || [],
      source: customer.source || '',
      status: customer.status,
      notes: customer.notes || '',
      email_opt_in: customer.email_opt_in ?? true,
      sms_opt_in: customer.sms_opt_in ?? true,
      whatsapp_opt_in: customer.whatsapp_opt_in ?? false
    });
    setIsDialogOpen(true);
  };

  const addTag = () => {
    if (tagInput && !formData.tags.includes(tagInput)) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      dormant: 'bg-yellow-100 text-yellow-700',
      do_not_contact: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Customers</h2>
          <p className="text-gray-500">Central customer database with duplicate detection</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-gradient-to-r from-green-600 to-teal-600" data-testid="create-customer-button">
              <Plus className="w-4 h-4" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Create Customer'}</DialogTitle>
            </DialogHeader>
            
            {duplicateCheck && duplicateCheck.is_duplicate && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-yellow-900">Potential Duplicates Found</h4>
                    <p className="text-sm text-yellow-800 mb-2">Match reasons: {duplicateCheck.match_reasons.join(', ')}</p>
                    <div className="space-y-2">
                      {duplicateCheck.matches.map(match => (
                        <div key={match.id} className="bg-white p-2 rounded border border-yellow-200 text-sm">
                          <strong>{match.name}</strong> - {match.email} - {match.phone}
                        </div>
                      ))}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-2"
                      onClick={handleContinueAnyway}
                    >
                      Continue Anyway
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Customer Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="customer-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="customer-phone-input"
                  />
                </div>
                
                {/* Address Autocomplete Section */}
                <div className="col-span-2">
                  <Label>Address Lookup</Label>
                  <AddressAutocomplete
                    onAddressSelect={handleAddressSelect}
                    getAuthHeaders={getAuthHeaders}
                    placeholder="Start typing to search UK addresses..."
                    initialValue={editingCustomer ? `${formData.address_1} ${formData.address_2} ${formData.city}`.trim() : ''}
                  />
                </div>
                
                {/* Manual Address Fields - Auto-populated by address lookup */}
                <div>
                  <Label htmlFor="address_1">Address Line 1</Label>
                  <Input
                    id="address_1"
                    value={formData.address_1}
                    onChange={(e) => setFormData({ ...formData, address_1: e.target.value })}
                    placeholder="House number and street name"
                  />
                </div>
                <div>
                  <Label htmlFor="address_2">Address Line 2</Label>
                  <Input
                    id="address_2"
                    value={formData.address_2}
                    onChange={(e) => setFormData({ ...formData, address_2: e.target.value })}
                    placeholder="Area or district (optional)"
                  />
                </div>
                <div>
                  <Label htmlFor="address_3">Address Line 3</Label>
                  <Input
                    id="address_3"
                    value={formData.address_3}
                    onChange={(e) => setFormData({ ...formData, address_3: e.target.value })}
                    placeholder="Additional address info (optional)"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City/Town</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., London, Manchester"
                  />
                </div>
                <div>
                  <Label htmlFor="county">County</Label>
                  <Select value={formData.county} onValueChange={(value) => setFormData({ ...formData, county: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select county" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {counties.map(county => (
                        <SelectItem key={county} value={county}>{county}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input
                    id="postcode"
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    placeholder="SW1A 1AA"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_type">Type</Label>
                  <Select value={formData.customer_type} onValueChange={(value) => setFormData({ ...formData, customer_type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="source">Lead Source</Label>
                  <Select value={formData.source} onValueChange={(value) => {
                    if (value === '__add_new__') {
                      setIsSourceDialogOpen(true);
                    } else {
                      setFormData({ ...formData, source: value });
                    }
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select lead source" />
                    </SelectTrigger>
                    <SelectContent>
                      {leadSources.map((source) => (
                        <SelectItem key={source.id} value={source.name}>
                          {source.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_new__" className="font-medium text-blue-600">
                        + Add New Source
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="dormant">Dormant</SelectItem>
                      <SelectItem value="do_not_contact">Do Not Contact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add tag (e.g., fleet, ceramic)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" size="sm" onClick={addTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Consent (GDPR)</Label>
                <div className="space-y-2 mt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.email_opt_in}
                      onChange={(e) => setFormData({ ...formData, email_opt_in: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Email Marketing</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.sms_opt_in}
                      onChange={(e) => setFormData({ ...formData, sms_opt_in: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">SMS Marketing</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.whatsapp_opt_in}
                      onChange={(e) => setFormData({ ...formData, whatsapp_opt_in: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">WhatsApp Marketing</span>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="customer-submit-button">
                  {editingCustomer ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Add New Source Dialog */}
      <Dialog open={isSourceDialogOpen} onOpenChange={setIsSourceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Lead Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="newSourceName">Source Name</Label>
              <Input
                id="newSourceName"
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                placeholder="e.g., LinkedIn, Trade Show, etc."
                onKeyPress={(e) => e.key === 'Enter' && handleAddNewSource()}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddNewSource} className="flex-1">
                Add Source
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsSourceDialogOpen(false);
                  setNewSourceName('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, phone, or postcode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="customer-search-input"
              />
            </div>
          </div>
          <div className="relative">
            <Select value={statusFilter || undefined} onValueChange={(value) => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="dormant">Dormant</SelectItem>
                <SelectItem value="do_not_contact">Do Not Contact</SelectItem>
              </SelectContent>
            </Select>
            {statusFilter && (
              <button
                onClick={() => setStatusFilter('')}
                className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear filter"
              >
                ×
              </button>
            )}
          </div>
          <div className="relative">
            <Select value={sourceFilter || undefined} onValueChange={(value) => setSourceFilter(value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {leadSources.map((source) => (
                  <SelectItem key={source.id} value={source.name}>
                    {source.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sourceFilter && (
              <button
                onClick={() => setSourceFilter('all')}
                className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear filter"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Filter Results Summary */}
      {(searchTerm || statusFilter || sourceFilter) && (
        <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
          Showing {filteredAndSortedCustomers.length} of {customers.length} customers
          {searchTerm && ` matching "${searchTerm}"`}
          {statusFilter && ` with status "${statusFilter}"`}
          {sourceFilter && ` from source "${sourceFilter}"`}
        </div>
      )}

      {/* Customer List */}
      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : customers.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No customers yet. Add your first customer!</p>
        </Card>
      ) : filteredAndSortedCustomers.length === 0 ? (
        <Card className="p-12 text-center">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No customers found matching the current filters.</p>
          <Button 
            variant="outline" 
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setSourceFilter('');
            }} 
            className="mt-4"
          >
            Clear Filters
          </Button>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('name')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Name {getSortIcon('name')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('email')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Email {getSortIcon('email')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('phone')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Phone {getSortIcon('phone')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('address')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Address {getSortIcon('address')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('status')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Status {getSortIcon('status')}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('source')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Source {getSortIcon('source')}
                  </Button>
                </TableHead>
                <TableHead className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('contact_count')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Contacts {getSortIcon('contact_count')}
                  </Button>
                </TableHead>
                <TableHead className="text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('vehicle_count')}
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Vehicles {getSortIcon('vehicle_count')}
                  </Button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedCustomers.map((customer) => (
                <TableRow 
                  key={customer.id} 
                  data-testid={`customer-row-${customer.id}`}
                  onClick={() => navigate(`/dashboard/customers/${customer.id}`)}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getCustomerTypeIcon(customer.customer_type)}
                      <div>
                        <div className="font-medium" data-testid={`customer-name-${customer.id}`}>
                          {customer.name}
                        </div>
                        {customer.tags && customer.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {customer.tags.slice(0, 2).map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                            ))}
                            {customer.tags.length > 2 && (
                              <Badge variant="secondary" className="text-xs">+{customer.tags.length - 2}</Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="text-sm">
                      {customer.email || <span className="text-gray-400">No email</span>}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="text-sm">
                      {customer.phone || <span className="text-gray-400">No phone</span>}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="text-sm">
                      {formatAddress(customer)}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <Badge className={getStatusColor(customer.status)}>
                      {customer.status}
                    </Badge>
                  </TableCell>
                  
                  <TableCell>
                    <div className="text-sm">
                      {customer.source ? (
                        <Badge variant="outline" className="text-xs">
                          {customer.source}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">No source</span>
                      )}
                    </div>
                  </TableCell>
                  
                  <TableCell className="text-center">
                    <Badge variant="secondary" className="font-mono">
                      {customer.contact_count || 0}
                    </Badge>
                  </TableCell>
                  
                  <TableCell className="text-center">
                    <Badge variant="secondary" className="font-mono">
                      {customer.vehicle_count || 0}
                    </Badge>
                  </TableCell>
                  
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        size="sm"
                        className="gap-1 bg-gradient-to-r from-blue-600 to-indigo-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/customers/${customer.id}`, { state: { openQuickQuote: true } });
                        }}
                        title="Quick Quote"
                      >
                        <Zap className="w-4 h-4" />
                        Quick Quote
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/customers/${customer.id}`);
                        }}
                        className="gap-1"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(customer);
                        }}
                        title="Edit Customer"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportCustomer(customer.id, customer.name);
                        }}
                        title="Export Customer"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDelete(customer.id)} 
                        className="text-red-600 hover:bg-red-50"
                        title="Delete Customer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default CustomersManagementEnhanced;
